import { useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../../services/TranslationService";
import CompanyService from "../../../../services/CompanyService";
import { ButtonTooltipIcon } from "../../../shared/components/ButtonTooltipIcon";
import AIBillingService, {
  AIBillingDateRange,
  AIBillingErrorKind,
  AIBillingUsageResponse,
  AI_BILLING_DEFAULT_PAGE_SIZE,
  getErrorKind
} from "../../../../services/AIBillingService";
import Paginator from "../../../shared/Paginator";
import AIBillingPeriodSelector, {
  AIBillingPeriodOption,
  exceedsMaxRange,
  getPeriodRange
} from "./components/AIBillingPeriodSelector";
import AIBillingUsageTable from "./components/AIBillingUsageTable";
import AIBillingStateBlock from "./components/AIBillingStateBlock";
import { navigateToAIBillingTab } from "./AIBillingNavigation";
import { useUsageExport } from "./AIBillingExport";

const AIAgentUsageDetail = () => {
  const { translate } = TranslationService;

  const [data, setData] = useState<AIBillingUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AIBillingErrorKind>("none");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(AI_BILLING_DEFAULT_PAGE_SIZE);
  const [periodOption, setPeriodOption] =
    useState<AIBillingPeriodOption>("current");
  const [customRange, setCustomRange] = useState<AIBillingDateRange>({});

  const range =
    periodOption === "custom" ? customRange : getPeriodRange(periodOption);
  const rangeIncomplete = !range.from || !range.to;
  const rangeTooLong = exceedsMaxRange(range);

  const fetchUsage = useCallback(
    async (dateRange: AIBillingDateRange, page: number, size: number) => {
      if (!dateRange.from || !dateRange.to) {
        return;
      }
      setLoading(true);
      setError("none");
      const result = await AIBillingService.getUsage({
        from: dateRange.from,
        to: dateRange.to,
        page,
        pageSize: size
      });
      setLoading(false);
      if (result instanceof Error) {
        setError(getErrorKind(result));
        setData(null);
        return;
      }
      setData(result);
    },
    []
  );

  useEffect(() => {
    if (rangeIncomplete || rangeTooLong) {
      return;
    }
    fetchUsage(range, currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchUsage,
    periodOption,
    customRange,
    currentPage,
    pageSize,
    rangeIncomplete,
    rangeTooLong
  ]);

  const retry = useCallback(
    () => fetchUsage(range, currentPage, pageSize),
    [fetchUsage, range, currentPage, pageSize]
  );
  const { exporting, onExport } = useUsageExport(range);

  const onPeriodChange = (option: AIBillingPeriodOption) => {
    setCurrentPage(0);
    setPeriodOption(option);
  };

  const onCustomRangeChange = (newRange: AIBillingDateRange) => {
    setCurrentPage(0);
    setCustomRange(newRange);
  };

  const items = data ? (data.items ?? data.list ?? []) : [];

  return (
    <div style={{ minWidth: 0 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0" style={{ fontWeight: 500 }}>
          {translate.AIPeriodUsage}
        </h5>
        <div className="d-flex align-content-center gap-3">
          {CompanyService.canDo("export") && (
            <ButtonTooltipIcon
              title={translate.Export}
              icon="fa-light fa-arrow-down-to-line"
              onClick={onExport}
              isLink={false}
              loading={exporting}
              animation={false}
            />
          )}
          <button
            className="btn btn-secondary"
            onClick={() => navigateToAIBillingTab("AIAgentUsage")}
          >
            {translate.AIBackToUsage}
          </button>
        </div>
      </div>

      <AIBillingPeriodSelector
        value={periodOption}
        onChange={onPeriodChange}
        customRange={customRange}
        onCustomRangeChange={onCustomRangeChange}
      />

      {rangeIncomplete ? (
        <div className="alert alert-info mb-0">
          {translate.AISelectDateRange}
        </div>
      ) : rangeTooLong ? (
        <div className="alert alert-warning mb-0">
          {translate.AIUsageRangeTooLong}
        </div>
      ) : (
        <div className="card">
          <AIBillingStateBlock
            loading={loading}
            error={error}
            isEmpty={data !== null && items.length === 0}
            onRetry={retry}
            emptyMessage={translate.AINoUsageYet}
            height={280}
          >
            {data && (
              <>
                <AIBillingUsageTable
                  items={items}
                  showTotalTokens
                  roundedBottom={false}
                />
                <Paginator
                  isTable
                  pageCount={Math.ceil(data.itemCount / data.pageSize)}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  loading={loading}
                  error={error !== "none"}
                  response={{
                    pageSize: data.pageSize,
                    itemCount: data.itemCount
                  }}
                />
              </>
            )}
          </AIBillingStateBlock>
        </div>
      )}
    </div>
  );
};

export default AIAgentUsageDetail;
